/**
 * Saved column-order application for descriptor-driven grids.
 *
 * A user's drag-reorder persists as an ordered field list (user preference
 * `grid.<persistKey>.ct<customerTypeId>.columnOrder`); this module re-applies it to the
 * descriptor's column-def array. MUI derives column order from the columns
 * prop, so the saved list stays the single source of truth for the columns it
 * names, and the descriptor decides where the rest belong.
 *
 * Because the saved list outranks the descriptor, it is stored against a stamp
 * of the column set it was saved for. When the backend changes which columns a
 * grid has, or the order they default to, the stamp stops matching and the
 * saved order is dropped — otherwise a list saved a year ago keeps overriding
 * the new arrangement, and only for the users who ever dragged a column, which
 * reads as the grid ignoring the backend.
 *
 * Contains:
 * - columnSetStamp — the descriptor column set's identity
 * - stampSavedColumnOrder — ordered fields + stamp → the value to persist
 * - readSavedColumnOrder — the saved value, or null when it no longer applies
 * - applyColumnOrder — pure column-def reorder
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * An identity for the descriptor's column set: FNV-1a over the field names in
 * descriptor order, so a gained column, a lost one and a changed default order
 * each produce a different stamp. Hashed rather than stored as the field list
 * itself to keep the saved preference small on grids with many columns.
 *
 * @param {?Array<{field: string}>} columns - column defs in descriptor order
 * @returns {?string} Null while there are no columns to identify.
 */
export function columnSetStamp(columns) {
  if (!Array.isArray(columns) || columns.length === 0) return null;
  const identity = columns.map(col => col?.field).join('|');
  let hash = 0x811c9dc5;
  for (let index = 0; index < identity.length; index += 1) {
    hash ^= identity.charCodeAt(index);
    // FNV-1a's 32-bit prime, by shifts: Math.imul keeps the product in int32
    // where a plain `*` would lose precision past 2^53.
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/**
 * The value to persist for a drag-reorder: the fields, plus the stamp of the
 * column set they were arranged against.
 *
 * @param {string[]} orderedFields
 * @param {?string} stamp - from columnSetStamp
 * @returns {{fields: string[], stamp: ?string}}
 */
export function stampSavedColumnOrder(orderedFields, stamp) {
  return { fields: orderedFields, stamp };
}

/**
 * The saved field order to apply, or null when it must not be applied: it was
 * saved against a different column set, or it predates stamping entirely (a
 * bare array, which may be any number of descriptor changes old).
 *
 * @param {*} saved - the persisted preference value, in either shape
 * @param {?string} stamp - the current column set's stamp
 * @returns {?string[]}
 */
export function readSavedColumnOrder(saved, stamp) {
  if (!stamp || !saved || Array.isArray(saved)) return null;
  if (saved.stamp !== stamp || !Array.isArray(saved.fields)) return null;
  return saved.fields;
}

/**
 * Reorder a column-def array by a saved ordered-field list.
 *
 * A column the saved order has never heard of — one the descriptor gained, or
 * one a customer type was newly granted — keeps the place the descriptor gave
 * it, immediately after whichever saved column it followed there. Appending
 * such columns at the end instead pushes them past the trailing actions
 * control, so a newly granted column shows up after Actions for every user
 * who has ever dragged a column.
 *
 * @param {Array}  columns       - GridColDef array in descriptor order
 * @param {?Array} orderedFields - saved field order (readSavedColumnOrder), or
 *   null/empty for none
 * @returns {Array}
 */
export function applyColumnOrder(columns, orderedFields) {
  if (!Array.isArray(orderedFields) || orderedFields.length === 0) return columns;
  const rank = new Map(orderedFields.map((field, index) => [field, index]));
  const known = columns.filter(col => rank.has(col.field));
  if (known.length === 0) return columns;
  known.sort((a, b) => rank.get(a.field) - rank.get(b.field));

  // Which saved column each unknown one trailed in the descriptor. Unknowns
  // ahead of every saved column lead, since they trailed nothing.
  const leading = [];
  const trailing = new Map();
  let anchor = null;
  for (const col of columns) {
    if (rank.has(col.field)) {
      anchor = col.field;
    } else if (anchor === null) {
      leading.push(col);
    } else {
      if (!trailing.has(anchor)) trailing.set(anchor, []);
      trailing.get(anchor).push(col);
    }
  }

  const out = [...leading];
  for (const col of known) {
    out.push(col, ...(trailing.get(col.field) ?? []));
  }
  return out;
}
